/* eslint-disable max-len */
/* eslint-disable react/no-unstable-nested-components */
import React, {
    Suspense, lazy, useEffect, useState, useContext,
} from 'react';
import './App.scss';
import {
    BrowserRouter as Router, Switch, Route, useLocation,
} from 'react-router-dom';
import AuthRoute from './components/AuthRoute';
import Loading from './components/Loading';
import useNetwork from './customHooks/useNetwork';
import syncPendingRecordsOnOfflineDB from './offlineDB/syncPendingItems';
import syncOfflineDB from './offlineDB/syncOfflineDB';
import WalkthroughTour from './components/WalkthroughTour';
import AIChat from './components/AIChat';
import { Provider as CustomAlertProvider } from './components/CustomAlert';
import { consumeServiceWorkerUpdatedFlag } from './utils/serviceWorkerUpdate';
import { getAccessToken, SESSION_CHANGE_EVENT } from './utils/accessToken';

// contexts
import WalkthroughTourProvider from './contexts/WalkthroughTour';
import SearchPageProvider from './contexts/SearchPage';
import SearchProvider from './contexts/Search';
import FarmsProvider from './contexts/Farms';
import ActiveFarmProvider from './contexts/ActiveFarm';
import { InstallButtonContext } from './contexts/InstallButton';
import HerdProvider from './contexts/Herd';
import UserContextProvider from './contexts/User';
import { useToast } from './contexts/Toast';
import useAutoLoadUser from './customHooks/useAutoLoadUser';

// import Layout from './components/Layout';
const Signup = lazy(() => import('./pages/Signup'));
const SignupWithInvite = lazy(() => import('./pages/SignupWithInvite'));
const Activity24hr = lazy(() => import('./pages/Activity24hr'));
const Herd = lazy(() => import('./pages/Herd'));
const Projects = lazy(() => import('./pages/Projects'));
const Project = lazy(() => import('./pages/Projects/Project'));
const ProjectAnimal = lazy(() => import('./pages/Projects/Project/Animal'));
const ProjectAnimalsWithDevices = lazy(() => import('./pages/Projects/Project/ProjectAnimalsWithDevices'));
const ProjectAnimals = lazy(() => import('./pages/Projects/Project/ProjectAnimals'));
const ProjectGroup = lazy(() => import('./pages/Projects/Project/ProjectGroup'));
const CreateAutoList = lazy(() => import('./pages/CreateAutoList'));
const AutoList = lazy(() => import('./pages/AutoList'));
const AutoListFilterLogic = lazy(() => import('./pages/AutoList/AutoListFilterLogic'));
const Group = lazy(() => import('./pages/Group'));
const Add = lazy(() => import('./pages/Add'));
// eslint-disable-next-line import/extensions
const Logs = lazy(() => import('./pages/Logs'));
const Login = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfServices = lazy(() => import('./pages/TermsOfServices'));
const OfflineLearnMore = lazy(() => import('./pages/OfflineLearnMore'));
const InstallOniOS = lazy(() => import('./pages/InstallOniOS'));
const Animal = lazy(() => import('./pages/Animal'));
const Devices = lazy(() => import('./pages/Devices'));
const Heat = lazy(() => import('./pages/Heat'));
const AllAnimals = lazy(() => import('./pages/AllAnimals'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Account = lazy(() => import('./pages/Account'));
const FarmSettings = lazy(() => import('./pages/FarmSettings'));
const WelfareAlerts = lazy(() => import('./pages/WelfareAlerts'));
const ProjectDevices = lazy(() => import('./pages/Projects/Project/ProjectDevices'));
const Support = lazy(() => import('./pages/Support'));
const Help = lazy(() => import('./pages/Help'));

const App = () => {
    const isOnline = useNetwork();
    const [hasSession, setHasSession] = useState(() => Boolean(getAccessToken()));
    const { setInstallEvent, setDisplayMode } = useContext(InstallButtonContext);

    useEffect(() => {
        const readSession = () => setHasSession(Boolean(getAccessToken()));
        window.addEventListener(SESSION_CHANGE_EVENT, readSession);
        return () => window.removeEventListener(SESSION_CHANGE_EVENT, readSession);
    }, []);

    const setPWADisplayMode = () => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
        if (document.referrer.startsWith('android-app://')) {
            setDisplayMode('twa');
        } else if (navigator.standalone || isStandalone) {
            setDisplayMode('standalone');
        } else {
            setDisplayMode('browser');
        }
    };

    useEffect(() => {
        setPWADisplayMode();
        window.addEventListener('beforeinstallprompt', (e) => {
            // Prevent the mini-infobar from appearing on android devices
            e.preventDefault();
            // save install event to use later on install button
            setInstallEvent(e);
        });

        // Initialize display ear tag length preference if not set
        if (!localStorage.getItem('ZELP_eartag_length_preference')) {
            localStorage.setItem('ZELP_eartag_length_preference', '4'); // Default to 4 digits
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let updatePendingInterval = null; // for sync local pending items -> server | about every 5 min
        let updateInterval = null; // for sync server -> local | about every 10 min
        if (isOnline && hasSession) {
            syncPendingRecordsOnOfflineDB(); // local items -> server
            syncOfflineDB(); // server -> local
            updatePendingInterval = setInterval(() => {
                syncPendingRecordsOnOfflineDB();
            }, 270000); // about 4.5 min
            updateInterval = setInterval(() => {
                syncOfflineDB();
            }, 540000); // about 9 min
            // (about 9 min to avoid from pass on 10 min check - if we set is to 10 min,
            // since we also checking 10 min to update in syncOfflineDB, it can pass
            // because of a few secound delay of db processes, so I'm setting this to 9 min)
        }
        return () => {
            updatePendingInterval && clearInterval(updatePendingInterval);
            updateInterval && clearInterval(updateInterval);
        };
    }, [isOnline, hasSession]);

    return (
        <Router>
            <CustomAlertProvider>
                <ServiceWorkerUpdateNotifier />
                <WalkthroughTourProvider>
                    <WalkthroughTour>
                        <UserContextProvider>
                            <AutoUserLoader>
                                <SearchPageProvider>
                                    <SearchProvider>
                                        <FeatureFlagProvider>
                                            <div className={`router-div
                           ${(navigator?.userAgentData?.platform === 'iPhone' || navigator?.platform === 'iPhone')
                                                    && navigator.userAgent?.match(/safari/i)
                                                    && (navigator.standalone ||
                                                        window.matchMedia('(display-mode: standalone)')
                                                            .matches) ? 'router-div__safari-url-closed' : ''}`}>
                                                <Suspense fallback={<div><Loading isFullPage /></div>}>
                                                    <Switch>
                                                        <Route path="/login" component={Login} />
                                                        <Route path="/reset-password" component={ResetPassword} />
                                                        <Route path="/signup-by-invite" component={SignupWithInvite} />
                                                        <Route path="/signup" component={Signup} />
                                                        <Route path="/privacy-policy" component={PrivacyPolicy} />
                                                        <Route path="/terms-of-services" component={TermsOfServices} />
                                                        <Route
                                                            path="/learn-more-about-offline-data"
                                                            component={OfflineLearnMore} />
                                                        <Route path="/install-ios-app" component={InstallOniOS} />
                                                        {/* DISABLED: <Route path="/tb-test" component={TbTest} /> */}

                                                        <FarmsProvider>
                                                            <ActiveFarmProvider>
                                                                <HerdProvider>
                                                                    <Switch>
                                                                        <AuthRoute path="/" exact component={Herd} />
                                                                        <AuthRoute
                                                                            path="/farms/:farmId/groups/:id"
                                                                            component={Group} />
                                                                        <AuthRoute
                                                                            key="/farms/:farmId/create-list"
                                                                            path="/farms/:farmId/create-list"
                                                                            component={CreateAutoList} />
                                                                        <AuthRoute
                                                                            path={
                                                                                '/farms/:farmId/auto-lists/'
                                                                                + ':id/filter-logic'
                                                                            }
                                                                            component={AutoListFilterLogic} />
                                                                        <AuthRoute
                                                                            path="/farms/:farmId/auto-lists/:id"
                                                                            component={AutoList} />
                                                                        <AuthRoute
                                                                            path="/farms/:farmId/animals/:id"
                                                                            component={Animal} />
                                                                        <AuthRoute
                                                                            path="/farms/:farmId/animals"
                                                                            component={AllAnimals} />
                                                                        <AuthRoute
                                                                            path="/projects/:id/groups/:groupId"
                                                                            component={ProjectGroup} />
                                                                        <AuthRoute
                                                                            path="/projects/:id/animals/:animalId"
                                                                            component={ProjectAnimal} />
                                                                        <AuthRoute
                                                                            path="/projects/:id/animals-with-devices"
                                                                            component={ProjectAnimalsWithDevices} />
                                                                        <AuthRoute
                                                                            path="/projects/:id/animals"
                                                                            component={ProjectAnimals} />
                                                                        <AuthRoute
                                                                            path="/projects/:id/devices"
                                                                            component={ProjectDevices} />
                                                                        <AuthRoute
                                                                            path="/projects/:id"
                                                                            component={Project} />
                                                                        <AuthRoute
                                                                            key="/projects"
                                                                            path="/projects"
                                                                            component={Projects} />
                                                                        <AuthRoute
                                                                            key="/farms/:farmId/lists/24hr-activity"
                                                                            path="/farms/:farmId/lists/24hr-activity"
                                                                            component={Activity24hr} />
                                                                        <AuthRoute
                                                                            key="/data/devices"
                                                                            path="/data/devices"
                                                                            component={Devices} />
                                                                        <AuthRoute
                                                                            key="/farms/:farmId/heat"
                                                                            path="/farms/:farmId/heat"
                                                                            component={Heat} />
                                                                        <AuthRoute
                                                                            key="/farms/:farmId/welfare-alerts"
                                                                            path="/farms/:farmId/welfare-alerts"
                                                                            component={WelfareAlerts} />
                                                                        <AuthRoute
                                                                            key="/add"
                                                                            path="/add"
                                                                            component={Add} />
                                                                        <AuthRoute
                                                                            key="/support"
                                                                            path="/support"
                                                                            component={Support} />
                                                                        <AuthRoute
                                                                            key="/help-sub-article"
                                                                            path="/help/:topicId/:subArticleId"
                                                                            component={Help} />
                                                                        <AuthRoute
                                                                            key="/help-topic"
                                                                            path="/help/:topicId"
                                                                            component={Help} />
                                                                        <AuthRoute
                                                                            key="/help"
                                                                            path="/help"
                                                                            exact
                                                                            component={Help} />

                                                                        <AuthRoute
                                                                            path={'/herd/*'}
                                                                            component={NotFound} />
                                                                        <AuthRoute
                                                                            key="/herd"
                                                                            path="/herd"
                                                                            component={Herd} />
                                                                        <AuthRoute
                                                                            key="/farms"
                                                                            path="/farms"
                                                                            component={Herd} />
                                                                        <AuthRoute
                                                                            key="/farms/:farmId"
                                                                            path="/farms/:farmId"
                                                                            component={Herd} />

                                                                        <AuthRoute path="/logs" component={Logs} />

                                                                        <AuthRoute
                                                                            path="/account"
                                                                            component={Account} />
                                                                        <AuthRoute
                                                                            path="/farm-settings"
                                                                            component={FarmSettings} />
                                                                        <AuthRoute path="*" component={NotFound} />

                                                                    </Switch>
                                                                    <AIChat />
                                                                </HerdProvider>
                                                            </ActiveFarmProvider>
                                                        </FarmsProvider>
                                                    </Switch>
                                                </Suspense>
                                            </div>
                                        </FeatureFlagProvider>
                                    </SearchProvider>
                                </SearchPageProvider>
                            </AutoUserLoader>
                        </UserContextProvider>
                    </WalkthroughTour>
                </WalkthroughTourProvider>
            </CustomAlertProvider>
        </Router>
    );
};

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const AutoUserLoader = (props) => {
    useAutoLoadUser(); // Automatically load user data on app start
    return props.children;
};

// A new service worker version is applied and reloaded automatically
// (see index.js). This tells the user, after the fact, why the page reloaded.
const ServiceWorkerUpdateNotifier = () => {
    const { successToast } = useToast();

    useEffect(() => {
        if (!consumeServiceWorkerUpdatedFlag()) return;
        successToast('The app was updated to the latest version.', {
            title: 'Updated',
            duration: 5000,
        });
    }, [successToast]);

    return null;
};

const FeatureFlagProvider = (props) => {
    const query = useQuery();
    useEffect(() => {
        const featureAnimalMap = query.get('feature_animal_map');
        const featureProjects = query.get('projects');
        const newSearch = query.get('new_search');
        const manageAccess = query.get('manage_access');
        window.featureFlags = {
            animalMap: !!featureAnimalMap,
            projects: !!featureProjects,
            newSearch: !!newSearch,
            manageAccess: !!manageAccess,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{props.children}</>;
};

export default App;
